import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Droplets, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { MUD_LEVELS, levelMeta } from "./MudBadge";

export default function MudMeterSection({ trailId }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [pickedLevel, setPickedLevel] = useState(2);

  const cond = useQuery({
    queryKey: ["conditions", trailId],
    queryFn: async () => (await api.get(`/trails/${trailId}/conditions`)).data,
  });

  const submitMut = useMutation({
    mutationFn: async (mud_level) => (await api.post(`/trails/${trailId}/conditions`, { mud_level })).data,
    onSuccess: () => {
      toast.success("Thanks — conditions logged");
      qc.invalidateQueries({ queryKey: ["conditions", trailId] });
    },
    onError: (e) => {
      const msg = e?.response?.data?.detail || "Couldn't submit conditions";
      toast.error(msg);
    },
  });

  const meta = levelMeta(cond.data?.mud_level_avg);
  const count = cond.data?.count ?? 0;

  return (
    <section className="td-card p-5 sm:p-6 mt-5" data-testid="mud-meter-section">
      <div className="flex items-center gap-2">
        <Droplets size={16} className="text-brand-primary" />
        <h3 className="font-heading text-lg font-medium">Mud Meter</h3>
        <span className="text-xs text-brand-subtle ml-auto">rolling 14 days · {count} report{count === 1 ? "" : "s"}</span>
      </div>

      {/* Current */}
      <div className="mt-3 flex items-center gap-3">
        {meta && count > 0 ? (
          <>
            <div className={`px-3 py-1.5 rounded-full text-sm font-semibold border ${meta.color} ${meta.text} ${meta.border}`}>
              <span className="mr-1">{meta.emoji}</span> {meta.label}
            </div>
            <div className="text-sm text-brand-subtle">
              avg <span className="text-brand-text font-medium">{cond.data.mud_level_avg.toFixed(1)}</span> / 4
            </div>
          </>
        ) : (
          <div className="text-sm text-brand-subtle">No recent reports — be the first to log conditions.</div>
        )}
      </div>

      {/* Slider */}
      <div className="mt-5">
        <div className="td-section-label">Report what you found</div>
        <div className="mt-3 grid grid-cols-4 gap-2" data-testid="mud-slider">
          {MUD_LEVELS.map((lvl) => {
            const active = pickedLevel === lvl.value;
            return (
              <button
                key={lvl.value}
                data-testid={`mud-level-${lvl.value}`}
                type="button"
                onClick={() => setPickedLevel(lvl.value)}
                disabled={!user}
                className={
                  "rounded-2xl border px-2 py-3 text-xs font-semibold transition-all flex flex-col items-center gap-1 " +
                  (active
                    ? `${lvl.color} ${lvl.text} ${lvl.border} shadow-md scale-[1.02]`
                    : "bg-brand-surface text-brand-subtle border-brand-border hover:border-brand-primary/40")
                }
              >
                <span className="text-base leading-none">{lvl.emoji}</span>
                <span>{lvl.value} · {lvl.label}</span>
              </button>
            );
          })}
        </div>

        {!user && (
          <p className="text-xs text-brand-subtle mt-3">Sign in to submit conditions.</p>
        )}

        <button
          data-testid="mud-submit"
          type="button"
          disabled={!user || submitMut.isPending}
          onClick={() => submitMut.mutate(pickedLevel)}
          className="td-btn-primary mt-4 w-full disabled:opacity-60"
        >
          {submitMut.isPending ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
          Submit conditions
        </button>
      </div>
    </section>
  );
}
